import z, { ZodTypeAny } from "zod";
import { ApiResponseDetails } from "./ApiResponseDetails";
import { Logger } from "cy-logging";
import { toJson } from "../toJson";

export class ApiResponse {
  fetchResponse: Response;
  method: string;
  startTimestamp: number;
  requestId: string;
  logger: Logger;

  constructor({
    fetchResponse,
    method,
    startTimestamp,
    requestId,
    logger,
  }: {
    fetchResponse: Response;
    method: string;
    startTimestamp: number;
    requestId: string;
    logger: Logger;
  }) {
    this.logger = logger;
    this.fetchResponse = fetchResponse;
    this.method = method;
    this.startTimestamp = startTimestamp;
    this.requestId = requestId;
  }

  get ok() {
    return this.fetchResponse.ok;
  }
  get status() {
    return this.fetchResponse.status;
  }

  async assertOkAsync(action: string) {
    if (!this.ok) {
      throw new Error(
        `Failed to ${action}: ${this.status} ${await this.bodyTextAsync()}`,
      );
    }
  }

  async bodyTextAsync() {
    return await this.fetchResponse.text();
  }

  async parseBodyAsync<T extends ZodTypeAny>(schema: T): Promise<z.infer<T>> {
    const logger = this.logger;
    const json = await toJson(this);
    try {
      return schema.parse(json) as z.infer<T>;
    } catch (error) {
      logger.error(
        "error parsing json",
        {
          json,
          zod: schema.description,
        },
        error,
      );
      throw error;
    }
  }

  public toLogMessage() {
    return [
      `method=${this.method}`,
      `status=${this.fetchResponse.status}`,
      `url=${this.fetchResponse.url}`,
      `timeTakenMs=${Date.now() - this.startTimestamp}`,
      `requestId=${this.requestId}`,
    ].join(", ");
  }
  public toLogDataObject() {
    return {
      method: this.method,
      status: this.fetchResponse.status,
      statusText: this.fetchResponse.statusText,
      ok: this.fetchResponse.ok,
      url: this.fetchResponse.url,
      timeTakenMs: Date.now() - this.startTimestamp,
      requestId: this.requestId,
    };
  }
  public async getJsonResponse() {
    const response = this.fetchResponse;
    const log = this.toLogDataObject();
    const logData: ApiResponseDetails = {
      ...log,
    };

    try {
      const bodyText = await response.text();
      if (bodyText) {
        logData.body = JSON.parse(bodyText);
      }
    } catch (err) {
      if (err !== logData) {
        logData.error = err;
      }
      logData.ok = false;
    }

    return logData;
  }
}
